import Button from '@atoms/Button/Button';
import Cta from '@atoms/Cta/Cta';
import Text from '@atoms/Text/Text';
import { IPageTitle, PageTitleSize } from '@models/IPageTitle';
import ThemeEnum from '@models/enums/ThemeEnum';
import VariantTypeEnum from '@models/enums/VariantTypeEnum';
import { fadeInUp } from '@utility/config/animations';
import classNames from 'classnames';
import { motion, useInView, Variants } from 'motion/react';
import { useRef } from 'react';

const PageTitle = ({
  site,
  backgroundColour,
  content,
  ctaStyle = VariantTypeEnum.Primary,
  isMainHeading = true,
  largeBodyText = false,
  links,
  theme,
  title,
  trumpetText,
  containerProps,
  hasPadding = true,
  shouldAnimate,
  reducedPadding = false,
  variant = PageTitleSize.Large,
}: IPageTitle) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const textRef = useRef(null);
  const inView = useInView(textRef, { amount: 0.5, once: true });

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        duration: 0.5,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <section
      {...containerProps}
      ref={sectionRef}
      className={classNames(
        'tw-relative',
        {
          'tw-dark': theme === ThemeEnum.Dark,
          'tw-py-[90px] md:tw-py-[126px]': hasPadding && !reducedPadding,
          'tw-pb-1 tw-pt-[90px] md:tw-pb-0': reducedPadding && hasPadding,
        },
        containerProps?.className
      )}
      style={
        isMainHeading || !shouldAnimate
          ? { backgroundColor: backgroundColour }
          : {}
      }
      data-background-color={shouldAnimate ? backgroundColour : undefined}
      data-main-heading={isMainHeading}
      data-is-page-title={true}
    >
      <div className="tw-grid-container">
        <motion.div
          className={classNames(
            'tw-grid-component-reduced-width tw-flex tw-flex-col tw-items-center tw-text-center tw-text-grey-100  dark:tw-text-white',
            variant === PageTitleSize.Large
              ? ' tw-gap-md md:tw-gap-lg'
              : 'tw-gap-sm'
          )}
          ref={textRef}
          initial={isMainHeading || !shouldAnimate ? 'visible' : 'hidden'}
          animate={inView || isMainHeading ? 'visible' : 'hidden'}
          variants={isMainHeading ? undefined : containerVariants}
        >
          <motion.div
            variants={isMainHeading || !shouldAnimate ? undefined : fadeInUp}
            className="tw-flex tw-flex-col tw-gap-sm"
          >
            {trumpetText && (
              <Text
                variant="body-small"
                className="tw-uppercase tw-text-grey-80 dark:tw-text-grey-20"
              >
                {trumpetText}
              </Text>
            )}
            <Text
              variant={
                variant === PageTitleSize.Large
                  ? 'hero-headline'
                  : 'ds-headline-2'
              }
              as={isMainHeading ? 'h1' : 'h2'}
            >
              {title}
            </Text>
          </motion.div>
          {content && (
            <motion.div
              variants={isMainHeading || !shouldAnimate ? undefined : fadeInUp}
            >
              <Text
                variant={largeBodyText ? 'ds-paragraph-large' : 'body'}
                className="tw-rich-text tw-text-grey-80 dark:tw-text-grey-20"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </motion.div>
          )}
          {!!links?.length && (
            <motion.div
              variants={isMainHeading || !shouldAnimate ? undefined : fadeInUp}
              className="tw-flex tw-flex-col tw-gap-md md:tw-flex-row"
            >
              {links.map(({ url, text, target }) =>
                ctaStyle === VariantTypeEnum.Primary ||
                ctaStyle === VariantTypeEnum.Secondary ? (
                  <Button
                    site={site}
                    as="link"
                    key={text}
                    href={url}
                    target={target}
                    label={text}
                    modifier={
                      ctaStyle === VariantTypeEnum.Secondary
                        ? 'outline'
                        : undefined
                    }
                  />
                ) : (
                  <Cta
                    site={site}
                    key={text}
                    url={url}
                    target={target}
                    text={text}
                  />
                )
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default PageTitle;
